import {
  Accordion,
  AccordionItem,
  Box,
  Button,
  Flex,
  SparklesIcon,
  Spinner,
  Text,
  toaster,
} from 'components'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router'
import { useBreakpoint } from 'utils'
import { FilterSelector } from '..'
import { LeadTableColumn, makeColumns, useEvent, useNewSessionCount } from '../../utils'
import {
  AnalyticsContentSection,
  AnalyticsSmartColumnsSection,
  AnalyticsUserResponsesSection,
  AnalyticsUserTraitsSection,
  EventAnalyticsOverviewSection,
} from '../analytics'

export default function EventAnalytics() {
  const { event, filters = [] } = useEvent()
  const newSessionCount = useNewSessionCount()
  const [loading, setLoading] = useState(true)
  const [columns, setColumns] = useState<LeadTableColumn[]>([])
  const [, setSearchParams] = useSearchParams()
  const isMobile = useBreakpoint() < 2

  useEffect(() => {
    if (!event) return
    makeColumns({ workspaceId: event.workspaceId, event }).then(columns => {
      setColumns(columns)
    })
  }, [event?.id])

  useEffect(() => {
    if (!newSessionCount) return

    toaster.create({
      title: 'New session',
      type: 'info',
      duration: 5000,
    })
  }, [newSessionCount])

  const items: AccordionItem[] = [
    {
      title: 'Overview',
      content: EventAnalyticsOverviewSection({ onLoaded: () => setLoading(false) }),
    },
    {
      title: (
        <Flex gap={4} align="center">
          <SparklesIcon color="primary.500" />
          <Text textStyle="bodyLarge">Smart Columns</Text>
        </Flex>
      ),
      content: AnalyticsSmartColumnsSection(),
    },
    {
      title: 'Content',
      content: AnalyticsContentSection(),
    },
    {
      title: 'User Traits',
      content: AnalyticsUserTraitsSection(),
    },
    {
      title: 'User Responses',
      content: AnalyticsUserResponsesSection(),
    },
  ].filter(({ content }) => content)

  return (
    <Flex w="100vw" direction="column" gap={5} pr={isMobile ? 2 : 5} overflowY="auto">
      {loading ? (
        <Flex justifyContent="center">
          <Spinner />
        </Flex>
      ) : (
        <Flex direction="column" gap={2} mt={4}>
          <Flex w="100%" align="center" justify="space-between">
            <Box flex={1} minW={0} />
            {filters?.length ? (
              <Flex
                gap={3}
                align="center"
                backgroundColor="primary.500"
                borderRadius="lg"
                p={2}
                pl={3}
              >
                <Text textStyle="bodySmall" color="white">
                  Underlying data has been filtered, check the sessions table to view underlying
                  data
                </Text>
                <Button onClick={() => setSearchParams({ view: 'sessions' })}>View Data</Button>
              </Flex>
            ) : (
              <Box flex={0} w={0} />
            )}
            {!isMobile && (
              <Flex flex={1} justify="flex-end" minW={0}>
                <FilterSelector columns={columns} />
              </Flex>
            )}
          </Flex>
          <Accordion
            key={items.length}
            multiple
            alignSelf="stretch"
            items={items}
            defaultValue={Array.from({ length: items.length }, (_, i) => i.toString())}
            buttonProps={{ backgroundColor: 'transparent' }}
          />
        </Flex>
      )}
    </Flex>
  )
}
